import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, CreditCard, Heart, Users } from 'lucide-react';
import PaymentMethodSelector from '../components/payment/PaymentMethodSelector';
import PadaidLogo from '../components/ui/PadaidLogo';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { trackEvent } from '../services/track';

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentV2 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // États du composant
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [amounts, setAmounts] = useState({ member: 9.99, supporter: 19.99 });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/payments/v2/config`);
        if (res.data) setAmounts({ member: res.data.member_monthly_amount, supporter: res.data.supporter_monthly_amount });
      } catch (e) { /* valeurs par défaut */ }
    })();
  }, []);

  // Packages de paiement disponibles
  const packages = [
    {
      id: 'member_monthly',
      name: 'Adhésion Membre',
      description: 'Adhésion mensuelle standard',
      amount: amounts.member,
      currency: '€',
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      recurring: true,
      popular: false
    },
    {
      id: 'supporter_monthly',
      name: 'Adhésion Supporter',
      description: 'Adhésion mensuelle supporter',
      amount: amounts.supporter,
      currency: '€',
      icon: Heart,
      color: 'from-purple-500 to-purple-600',
      recurring: true,
      popular: true
    },
    {
      id: 'donation_custom',
      name: 'Don ponctuel',
      description: 'Montant libre',
      amount: null,
      currency: '€',
      icon: Heart,
      color: 'from-green-500 to-green-600',
      recurring: false,
      popular: false
    },
    {
      id: 'donation_monthly',
      name: 'Don mensuel',
      description: 'Montant libre, chaque mois',
      amount: null,
      currency: '€',
      icon: Heart,
      color: 'from-rose-500 to-rose-600',
      recurring: true,
      popular: false
    }
  ];

  // Initialiser le package sélectionné depuis l'URL
  useEffect(() => {
    const packageParam = searchParams.get('package');
    if (packageParam) {
      const pkg = packages.find(p => p.id === packageParam);
      if (pkg) {
        setSelectedPackage(pkg);
      }
    }
  }, [searchParams, amounts]);

  useEffect(() => {
    // Emit payment_success when returning with a paid status (handled externally)
    try {
      const lastSession = sessionStorage.getItem('padaid_last_session');
      const url = new URL(window.location.href);
      if (url.pathname.includes('/payment/success') && lastSession) {
        trackEvent({ type: 'payment_success', url: window.location.href, meta: { session_id: lastSession } });
        sessionStorage.removeItem('padaid_last_session');
      }
    } catch (e) {}
  }, []);

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setError('');
  };

  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
  };

  const getPaymentMethodName = (method) => {
    const methods = {
      'card': 'Carte bancaire',
      'google_pay': 'Google Pay',
      'apple_pay': 'Apple Pay'
    };
    return methods[method] || 'Carte bancaire';
  };

  const validateForm = () => {
    if (!selectedPackage) {
      setError('Veuillez sélectionner un type de paiement');
      return false;
    }

    if (selectedPackage.amount === null && (!customAmount || parseFloat(customAmount) <= 0)) {
      setError('Veuillez saisir un montant valide pour le don');
      return false;
    }

    return true;
  };

  const handlePayment = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const paymentData = {
        package_type: selectedPackage.id,
        payment_method: selectedPaymentMethod,
        origin_url: window.location.origin,
        metadata: {
          user_agent: navigator.userAgent,
          payment_method_name: getPaymentMethodName(selectedPaymentMethod)
        }
      };

      // Ajouter le montant personnalisé si nécessaire
      if (selectedPackage.amount === null) {
        paymentData.custom_amount = parseFloat(customAmount);
      }

      try { trackEvent({ type: 'donation', url: window.location.href, meta: { package: selectedPackage?.id, method: selectedPaymentMethod } }); } catch(e){}
      const response = await axios.post(
        `${API}/api/payments/v2/checkout/session`,
        paymentData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.url) {
        try { sessionStorage.setItem('padaid_last_session', response.data.session_id || ''); } catch(e){}

        // Rediriger vers Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('URL de paiement non reçue');
      }

    } catch (error) {
      console.error('Erreur lors du paiement:', error);
      setError(
        error.response?.data?.detail || 
        'Erreur lors de la création de la session de paiement'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-primary/5">
      {/* Header */}
      <div className="container max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Retour</span>
          </Button>
          
          <PadaidLogo size="sm" />
        </div>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Soutenir PADAID</h1>
          <p className="text-muted-foreground">
            Choisissez votre type de soutien et votre méthode de paiement préférée
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Sélection du package */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CreditCard className="h-5 w-5" />
                <span>Type de soutien</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {packages.map((pkg) => {
                const IconComponent = pkg.icon;
                const isSelected = selectedPackage?.id === pkg.id;
                
                return (
                  <Card 
                    key={pkg.id}
                    className={`cursor-pointer transition-all duration-200 relative ${
                      isSelected ? 'ring-2 ring-primary border-primary' : 'hover:border-primary/50'
                    }`}
                    onClick={() => handlePackageSelect(pkg)}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-2 left-4 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                        Populaire
                      </div>
                    )}
                    
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-4">
                        <div className={`p-3 rounded-lg bg-gradient-to-r ${pkg.color}`}>
                          <IconComponent className="h-6 w-6 text-white" />
                        </div>
                        
                        <div className="flex-1">
                          <h4 className="font-semibold">{pkg.name}</h4>
                          <p className="text-sm text-muted-foreground">{pkg.description}</p>
                          {pkg.amount && (
                            <p className="text-lg font-bold text-primary mt-1">
                              {pkg.amount}{pkg.currency}/mois
                            </p>
                          )}
                        </div>
                        
                        <div className={`w-4 h-4 rounded-full border-2 ${
                          isSelected 
                            ? 'bg-primary border-primary' 
                            : 'border-muted-foreground'
                        }`}>
                          {isSelected && (
                            <div className="w-full h-full rounded-full bg-white scale-50" />
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Montant personnalisé pour les dons */}
              {selectedPackage?.amount === null && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2">
                    Montant du don (€)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="Ex: 25.00"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="text-lg"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Méthode de paiement et résumé */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Méthode de paiement</CardTitle>
              </CardHeader>
              <CardContent>
                <PaymentMethodSelector
                  selectedMethod={selectedPaymentMethod}
                  onMethodSelect={handlePaymentMethodSelect}
                />
              </CardContent>
            </Card>

            {/* Résumé */}
            {selectedPackage && (
              <Card>
                <CardHeader>
                  <CardTitle>Résumé</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span>{selectedPackage.name}</span>
                    <span className="font-semibold">
                      {selectedPackage.amount 
                        ? `${selectedPackage.amount}€`
                        : `${customAmount || '0'}€`
                      }
                    </span>
                  </div>
                  
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Méthode de paiement</span>
                    <span>{getPaymentMethodName(selectedPaymentMethod)}</span>
                  </div>
                  
                  <hr />
                  
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span>
                      {selectedPackage.amount 
                        ? `${selectedPackage.amount}€`
                        : `${customAmount || '0'}€`
                      }
                    </span>
                  </div>

                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <Button
                    onClick={handlePayment}
                    disabled={loading || !selectedPackage}
                    className="w-full"
                    size="lg"
                  >
                    {loading ? 'Traitement...' : 'Procéder au paiement'}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    En cliquant sur "Procéder au paiement", vous acceptez nos conditions d'utilisation
                    et vous serez redirigé vers notre processeur de paiement sécurisé Stripe.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentV2;